import { type FC, type ReactNode, useEffect, useMemo, useState } from 'react';

import { useCms, useContent } from '@eneco/cms';
import type { FlowThankYouStepsContent } from '@eneco/cms-types/generated/FlowThankYouSteps';
import { getBasket } from '@eneco/dc/services/ShoppingBasketPublicService';
import useDC from '@eneco/dc/useDC';
import { Bleed, Box, Carousel, Heading, IndexCircle, Stack } from '@eneco/sparky';
import {
  Calendar,
  Certificate,
  Meter,
  SmartMeter,
  Smartphone,
  SwitchContract,
} from '@eneco/sparky/illustrations';
import Cookies from 'js-cookie';

/** `EnergyType` values (`@eneco/flows/types`) that carry no electricity or gas contract to terminate. */
const WARMTH_ONLY_TYPES = new Set([
  'warmth',
  'warmthAndTapWater',
  'warmthAndCooling',
  'warmthAndTapWaterAndCooling',
  'tapwater',
]);

/** The slice of the energy flow's state, stored on the basket as `flowState`, that picks the steps. */
interface FlowState {
  energyType?: string;
  isMoving?: boolean;
  hasDoubleMeter?: boolean;
}

interface StepCard {
  id: string;
  description?: string;
  illustration: ReactNode;
}

/** Picks a description for a tri-state flag, falling back while the flag is still unknown. */
const pickDescription = <T,>(flag: boolean | undefined, whenTrue: T, whenFalse: T, fallback: T): T => {
  if (flag === undefined) return fallback;

  return flag ? whenTrue : whenFalse;
};

const parseFlowState = (flowState?: string | null): FlowState => {
  if (!flowState) return {};
  try {
    return JSON.parse(flowState);
  } catch {
    return {};
  }
};

/**
 * `FlowThankYouSteps` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors `apps/flowthankyousteps/nl/eneco/src/FlowThankYouSteps.tsx`: a heading above a carousel of
 * numbered steps, each picked from the finished energy flow's basket (warmth-only products skip the
 * contract-termination step; moving vs switching and a smart meter pick the description; until the
 * basket says, the fallback description shows). While a basket is being fetched nothing renders.
 *
 * On Sanity the eleven descriptions live in the `richText` family array (ADR-0068); the adapter
 * un-sweeps them to their plain names, so both lanes read the same `FlowThankYouStepsContent`.
 *
 * Known simplification: the legacy component reads the basket through `basketMachine`
 * (`@eneco/flows/machines`), and `lib:flows` is not in `lib:cms-components`'s allowed dependencies
 * (`eslint.config.mjs`). This block calls the same `getBasket` service directly instead. One side
 * effect of the machine is dropped: when the fetch fails, the machine creates a fresh, empty basket.
 * The rendered result is the same — an empty basket carries no flow state, so the fallbacks show —
 * but this lane does not write to the Digital Core from a thank-you page.
 *
 * The descriptions render as a bare `div` with the HTML inside, which is what the legacy
 * `@sitecore-jss` `RichText` emits, rather than through `@eneco/components`' `RichText`, which would
 * restyle every paragraph.
 */
const FlowThankYouSteps: FC = () => {
  const { adapter } = useCms();
  const content = useContent<FlowThankYouStepsContent>();
  const { businessUnit, label } = useDC();

  const { flowName, title } = content;
  const [flowState, setFlowState] = useState<FlowState>({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const basketId = flowName ? Cookies.get(`${flowName.toLowerCase()}_basket`) : undefined;
    if (!basketId) {
      setIsLoading(false);
      return;
    }

    getBasket({ businessUnit, label, id: basketId })
      .then(response => setFlowState(parseFlowState(response?.data?.attributes?.flowState)))
      .catch(() => setFlowState({}))
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- lifecycle componentDidMount
  }, []);

  const cards = useMemo<StepCard[]>(() => {
    const { energyType, isMoving, hasDoubleMeter } = flowState;
    const isWarmthOnly = energyType ? WARMTH_ONLY_TYPES.has(energyType) : false;

    return [
      {
        id: 'contract',
        description: content.contractStepDescription,
        illustration: <Certificate size="large" color="brandGradient" />,
      },
      {
        id: 'mobileApp',
        description: content.mobileAppStepDescription,
        illustration: <Smartphone size="large" color="brandGradient" />,
      },
      ...(!isWarmthOnly
        ? [
            {
              id: 'contractTermination',
              description: pickDescription(
                isMoving,
                content.contractTerminationStepCIDescription,
                content.contractTerminationStepWSDescription,
                content.contractTerminationStepFallbackDescription,
              ),
              illustration: <SwitchContract size="large" color="brandGradient" />,
            },
          ]
        : []),
      {
        id: 'reading',
        description: pickDescription(
          hasDoubleMeter,
          content.readingStepSmartDescription,
          content.readingStepDescription,
          content.readingStepFallbackDescription,
        ),
        illustration: hasDoubleMeter ? (
          <SmartMeter size="large" color="brandGradient" />
        ) : (
          <Meter size="large" color="brandGradient" />
        ),
      },
      {
        id: 'startDate',
        description: pickDescription(
          isMoving,
          content.startDateStepCIDescription,
          content.startDateStepWSDescription,
          content.startDateStepFallbackDescription,
        ),
        illustration: <Calendar size="large" color="brandGradient" />,
      },
    ]
      .map(card => ({ ...card, description: adapter.toRichText(card.description) }))
      .filter(card => card.description);
  }, [adapter, content, flowState]);

  if (isLoading) {
    return null;
  }

  return (
    <Stack gap="6">
      {title && (
        <Heading as="h2" size={{ initial: 'S', md: 'M' }}>
          {title}
        </Heading>
      )}
      <Bleed horizontal="gridGutter">
        <Carousel scrollMarginX="gridGutter" scrollBehavior={{ initial: 'snap', md: 'vertical' }}>
          {cards.map((card, index) => (
            <Carousel.Item key={card.id}>
              <Box padding="6">
                <Box paddingLeft="8">
                  <Stack alignX="start">
                    <IndexCircle size="S">{index + 1}</IndexCircle>
                  </Stack>
                </Box>
                <Stack gap="4" alignX="center">
                  {card.illustration}
                  <div dangerouslySetInnerHTML={{ __html: card.description ?? '' }} />
                </Stack>
              </Box>
            </Carousel.Item>
          ))}
        </Carousel>
      </Bleed>
    </Stack>
  );
};

export default FlowThankYouSteps;
